import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { toast } from "sonner";

export default function AuthPage({ mode = "login" }) {
  const [isLogin, setIsLogin] = useState(mode === "login");
  const [form, setForm] = useState({ name: "", email: "", password: "", dojo: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const path = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(path, payload);
      setUser(data.user);
      toast.success(isLogin ? "Welcome back" : "Account created");
      navigate("/library");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/library";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const input = (name, type, ph) => (
    <input
      data-testid={`auth-${name}`}
      type={type}
      placeholder={ph}
      value={form[name]}
      onChange={(e) => setForm({ ...form, [name]: e.target.value })}
      required
      className="w-full bg-transparent border border-white/20 focus:border-[#C5A059] outline-none px-4 py-3 text-[#EBE9E1] placeholder:text-[#555] rounded-sm transition-colors"
    />
  );

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="px-6 mx-auto max-w-md py-24 fade-up">
        <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-3">{isLogin ? "Welcome back" : "Create account"}</p>
        <h1 className="font-serif text-4xl text-[#EBE9E1] mb-8">{isLogin ? "Enter the Archive" : "Join the Archive"}</h1>

        <button data-testid="google-login-btn" onClick={googleLogin}
          className="btn-ghost w-full py-3 mb-6 flex items-center justify-center gap-3">
          <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="w-5 h-5" />
          Continue with Google
        </button>

        <div className="flex items-center gap-4 mb-6 text-[#555] text-xs">
          <div className="flex-1 h-px bg-white/10" /> OR <div className="flex-1 h-px bg-white/10" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {!isLogin && input("name", "text", "Full name")}
          {input("email", "email", "Email address")}
          {input("password", "password", "Password")}
          {!isLogin && input("dojo", "text", "Dojo affiliation (e.g. Valley Aikikai)")}
          {err && <p data-testid="auth-error" className="text-[#c96a6a] text-sm">{err}</p>}
          <button data-testid="auth-submit" type="submit" disabled={busy}
            className="btn-primary w-full py-3 font-medium disabled:opacity-50">
            {busy ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
          </button>
        </form>

        <p className="mt-6 text-[#8A8F98] text-sm">
          {isLogin ? "New to the archive?" : "Already a member?"}{" "}
          <button data-testid="auth-toggle" onClick={() => { setIsLogin(!isLogin); setErr(""); }}
            className="text-[#C5A059] hover:underline">
            {isLogin ? "Create an account" : "Sign in"}
          </button>
        </p>
        <p className="mt-2 text-[#8A8F98] text-sm">
          Free, full access during our beta — no payment required.
        </p>
      </div>
    </div>
  );
}
